import { useState, type ReactNode } from 'react';
import { Num, T, useGT } from 'gt-react';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  HUBBELL_FEEDBACK_DIVISIONS,
  RENEWAL_RULE_TYPES,
  type RenewalRuleType,
} from '@erp/rpc';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { readFileArrayBuffer } from '@/lib/daily-reports/readFileArrayBuffer';
import { FileDropzone } from '../../../item-master/components/FileDropzone';
import { RESULTS_ACCEPT } from '../resultsFile';
import {
  mergeItemLists,
  parseItemList,
  readItemListWorkbook,
} from './itemList';
import {
  ruleFromForm,
  type RenewalRuleDraft,
  type RenewalRuleFormError,
  type RenewalRuleFormState,
} from './renewalRuleForm';
import { divisionLabel, renewalRuleKindNames } from './renewalRuleText';

function isRuleType(value: unknown): value is RenewalRuleType {
  return RENEWAL_RULE_TYPES.some((type) => type === value);
}

type FormChange = (changes: Partial<RenewalRuleFormState>) => void;

interface Props {
  initial: RenewalRuleFormState;
  isSaving: boolean;
  onSave: (draft: RenewalRuleDraft) => void;
  onCancel: () => void;
}

/** The body of the Renewal Rule dialog: kind, name, Divisions, parameters. */
export function RenewalRuleEditor({
  initial,
  isSaving,
  onSave,
  onCancel,
}: Props) {
  const gt = useGT();
  const kindNames = renewalRuleKindNames(gt);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<RenewalRuleFormError | null>(null);

  function update(changes: Partial<RenewalRuleFormState>): void {
    setForm((current) => ({ ...current, ...changes }));
    setError(null);
  }

  /** A blank name, or the old kind's name, follows the kind. */
  function changeType(type: RenewalRuleType): void {
    const followsKind =
      form.name.trim() === '' || form.name === kindNames[form.type];
    update({ type, ...(followsKind ? { name: kindNames[type] } : {}) });
  }

  function handleSave(): void {
    const result = ruleFromForm(form);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    onSave(result.draft);
  }

  const ctx = { context: 'Mass Renewal rule editor validation error' };
  const errorMessages: Record<RenewalRuleFormError, string> = {
    name: gt('Enter a name (at most 120 characters).', ctx),
    tolerance: gt('Enter a tolerance in dollars between 0 and 1.', ctx),
    floor: gt('Enter a margin floor between 0% and 99%.', ctx),
    items: gt(
      'Enter at least one AS400 item, at most 40 characters each.',
      ctx,
    ),
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-end gap-2">
        <Field
          label={
            <T context="label of the Mass Renewal rule kind picker">Kind</T>
          }
        >
          <Select
            value={form.type}
            onValueChange={(value) => {
              if (isRuleType(value)) changeType(value);
            }}
          >
            <SelectTrigger className="h-8 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RENEWAL_RULE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {kindNames[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field
          label={<T context="label of the Mass Renewal rule name">Name</T>}
        >
          <Input
            className="h-8 w-64"
            value={form.name}
            onChange={(event) => update({ name: event.target.value })}
          />
        </Field>
      </div>

      <KindInputs form={form} onChange={update} />

      <DivisionsPicker
        divisions={form.divisions}
        onChange={(divisions) => update({ divisions })}
      />

      {error ? (
        <p className="text-destructive">{errorMessages[error]}</p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving}
        >
          <XIcon className="h-4 w-4" />
          <T context="button that cancels editing a Mass Renewal rule">
            Cancel
          </T>
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          <CheckIcon className="h-4 w-4" />
          <T context="button that saves a Mass Renewal rule">Save Rule</T>
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      {children}
    </label>
  );
}

/** The parameters of the chosen kind; one branch per kind. */
function KindInputs({
  form,
  onChange,
}: {
  form: RenewalRuleFormState;
  onChange: FormChange;
}) {
  switch (form.type) {
    case 'holdWhenCostUnchanged':
      return (
        <Field
          label={
            <T context="label of the Mass Renewal hold rule's cost tolerance input">
              Tolerance ($): cost counts as unchanged within this amount
            </T>
          }
        >
          <Input
            className="h-8 w-32"
            inputMode="decimal"
            value={form.tolerance}
            onChange={(event) => onChange({ tolerance: event.target.value })}
          />
        </Field>
      );
    case 'marginFloor':
      return (
        <Field
          label={
            <T context="label of the Mass Renewal margin floor input, a percent">
              Floor (%): a decrease never takes the margin below this
            </T>
          }
        >
          <Input
            className="h-8 w-32"
            inputMode="decimal"
            value={form.floorPercent}
            onChange={(event) => onChange({ floorPercent: event.target.value })}
          />
        </Field>
      );
    case 'holdListedItems':
      return <ItemListInput itemsText={form.itemsText} onChange={onChange} />;
  }
}

function ItemListInput({
  itemsText,
  onChange,
}: {
  itemsText: string;
  onChange: FormChange;
}) {
  const gt = useGT();
  const [isReading, setIsReading] = useState(false);
  const count = parseItemList(itemsText).length;

  async function importFile(file: File): Promise<void> {
    setIsReading(true);
    try {
      const imported = readItemListWorkbook(await readFileArrayBuffer(file));
      if (imported.length === 0) {
        toast.error(
          gt('No items found in the first column of {file}.', {
            file: file.name,
            context: 'toast when an imported Mass Renewal item list is empty',
          }),
        );
      } else {
        const merged = mergeItemLists(parseItemList(itemsText), imported);
        onChange({ itemsText: merged.join('\n') });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
    setIsReading(false);
  }

  return (
    <div className="flex flex-col gap-1">
      <Field
        label={
          <T context="label of the Mass Renewal held-items list">
            AS400 items to hold: one per line, or comma-separated
          </T>
        }
      >
        <Textarea
          className="h-40 font-mono text-xs"
          placeholder={gt('e.g. HUBHC6RPEB', {
            context: 'placeholder of the Mass Renewal held-items list',
          })}
          value={itemsText}
          onChange={(event) => onChange({ itemsText: event.target.value })}
        />
      </Field>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          <T context="count of distinct AS400 items in a Mass Renewal held-items list">
            <Num>{count}</Num> items
          </T>
        </span>
        <FileDropzone
          inline
          className="px-2 py-1"
          accept={RESULTS_ACCEPT}
          disabled={isReading}
          prompt={
            <T context="drop area that imports a Mass Renewal item list from the first column of a workbook">
              Import from Excel (first column)
            </T>
          }
          validate={(file) =>
            Promise.resolve(
              /\.(xlsx|xls)$/i.test(file.name)
                ? null
                : {
                    kind: 'unsupported',
                    title: gt('Unsupported file format', {
                      context:
                        'toast title when a non-Excel file is imported as a Mass Renewal item list',
                    }),
                    message: gt('Import an item list workbook (.xlsx).', {
                      context:
                        'toast message when a non-Excel file is imported as a Mass Renewal item list',
                    }),
                  },
            )
          }
          onFileAccepted={(file) => void importFile(file)}
        />
      </div>
    </div>
  );
}

function DivisionsPicker({
  divisions,
  onChange,
}: {
  divisions: string[];
  onChange: (divisions: string[]) => void;
}) {
  function toggle(letter: string, checked: boolean): void {
    onChange(
      checked
        ? [...divisions, letter]
        : divisions.filter((current) => current !== letter),
    );
  }
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="text-muted-foreground text-xs">
        <T context="label of the Mass Renewal rule Divisions picker; none checked means the rule applies to every Division">
          Divisions (none checked = All Divisions)
        </T>
      </legend>
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
        {HUBBELL_FEEDBACK_DIVISIONS.map(({ letter }) => (
          <label key={letter} className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={divisions.includes(letter)}
              onCheckedChange={(checked) => toggle(letter, checked === true)}
            />
            {divisionLabel(letter)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
